import {
  DMSans_400Regular,
  DMSans_500Medium,
  DMSans_600SemiBold,
  DMSans_700Bold,
} from '@expo-google-fonts/dm-sans';
import { GeistMono_400Regular, GeistMono_600SemiBold } from '@expo-google-fonts/geist-mono';
import { Manrope_400Regular, Manrope_500Medium } from '@expo-google-fonts/manrope';
import { Sora_600SemiBold, Sora_800ExtraBold } from '@expo-google-fonts/sora';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import Constants from 'expo-constants';
import { useFonts } from 'expo-font';
import { Stack, useNavigationContainerRef } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import { StatusBar } from 'expo-status-bar';
import { type ReactNode, useEffect, useState } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { type Edge, SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context';
import { ErrorBoundary } from '../src/components/ErrorBoundary';
import { OfflineBanner } from '../src/components/layout/OfflineBanner';
import { AuthProvider } from '../src/contexts/AuthContext';
import { LayoutChromeProvider } from '../src/contexts/LayoutChromeContext';
import { ModalProvider } from '../src/contexts/ModalContext';
import { OrgCacheReset } from '../src/contexts/OrgCacheReset';
import { ToastProvider } from '../src/contexts/ToastContext';
import { TranslationProvider, useTranslation } from '../src/contexts/TranslationContext';
import { useLanguageSync } from '../src/features/settings/hooks/useLanguageSync';
import { useReportVersion } from '../src/features/version-reporter';
import { useNotificationListeners } from '../src/hooks/useNotificationListeners';
import { useScreenTracking } from '../src/hooks/useScreenTracking';
import { AccountLanguageBridge } from '../src/i18n/AccountLanguageBridge';
import { bootstrapMixpanelConsent, getAnalyticsConsentWithin } from '../src/lib/analyticsConsent';
import { resolveAppEnv } from '../src/lib/appEnv';
import { createMixpanelClient } from '../src/lib/createMixpanelClient';
import { type MixpanelClient, MixpanelContext, useMixpanel } from '../src/lib/mixpanel';
import { asyncStoragePersister, dehydrateOptions, queryClient } from '../src/lib/queryPersistence';
import { initializeSentry, registerNavigationContainer, Sentry } from '../src/lib/sentry';
import { buildAppSuperProperties } from '../src/lib/superProperties';
import { colors } from '../src/theme/colors';

const MIXPANEL_TOKEN = process.env.EXPO_PUBLIC_MIXPANEL_TOKEN ?? '';

type BufferedClient = MixpanelClient & { flush(real: MixpanelClient): void };

function createBufferedClient(): BufferedClient {
  const buffer: Array<() => void> = [];
  const client: BufferedClient = {
    track: (event: string, props?: Record<string, unknown>): void =>
      void buffer.push(() => client.track(event, props)),
    identify: (id: string, props?: Record<string, unknown>): void =>
      void buffer.push(() => client.identify(id, props)),
    reset: (): void => void buffer.push(() => client.reset()),
    register: (props: Record<string, unknown>): void =>
      void buffer.push(() => client.register(props)),
    setGroup: (type: string, key: string, props?: Record<string, unknown>): void =>
      void buffer.push(() => client.setGroup(type, key, props)),
    screen: (name: string, props?: Record<string, unknown>): void =>
      void buffer.push(() => client.screen(name, props)),
    optInTracking: (): void => void buffer.push(() => client.optInTracking()),
    optOutTracking: (): void => void buffer.push(() => client.optOutTracking()),
    flush(real: MixpanelClient) {
      client.track = real.track;
      client.identify = real.identify;
      client.reset = real.reset;
      client.register = real.register;
      client.setGroup = real.setGroup;
      client.screen = real.screen;
      client.optInTracking = real.optInTracking;
      client.optOutTracking = real.optOutTracking;
      for (const call of buffer) call();
      buffer.length = 0;
    },
  };
  return client;
}

function RegisterMixpanelSuperProps() {
  const mp = useMixpanel();
  const { language } = useLanguageSync();

  useEffect(() => {
    mp.register(
      buildAppSuperProperties({
        platform: Platform.OS,
        appVersion: Constants.expoConfig?.version ?? 'unknown',
        appEnv: resolveAppEnv(),
        locale: language,
      }),
    );
  }, [mp, language]);

  return null;
}

function MaybeMixpanelProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [client, setClient] = useState<MixpanelClient>(createBufferedClient);

  useEffect(() => {
    if (!MIXPANEL_TOKEN) return;
    let cancelled = false;
    let realClient: MixpanelClient | undefined;
    let pendingLateConsent: boolean | undefined;
    let bootstrapped = false;
    const applyBootstrap = (real: MixpanelClient, grantedValue: boolean) => {
      if (bootstrapped) return;
      bootstrapped = true;
      setClient((prev) => {
        bootstrapMixpanelConsent(real, grantedValue, () => {
          if ('flush' in prev) {
            (prev as BufferedClient).flush(real);
          }
        });
        return real;
      });
    };
    const completeBootstrap = (lateGranted: boolean) => {
      if (cancelled) return;
      if (!realClient) {
        pendingLateConsent = lateGranted;
        return;
      }
      applyBootstrap(realClient, lateGranted);
    };
    void (async () => {
      const [{ granted, provisional }, real] = await Promise.all([
        getAnalyticsConsentWithin(undefined, completeBootstrap),
        createMixpanelClient(MIXPANEL_TOKEN, { debug: __DEV__ }),
      ]);
      if (cancelled) return;
      realClient = real;
      if (pendingLateConsent !== undefined) {
        applyBootstrap(real, pendingLateConsent);
        return;
      }
      if (provisional) return;
      applyBootstrap(real, granted);
    })().catch((error) => {
      console.warn('Mixpanel initialization failed:', error);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return <MixpanelContext.Provider value={client}>{children}</MixpanelContext.Provider>;
}

SplashScreen.preventAutoHideAsync();
initializeSentry();

const persistOptions = { persister: asyncStoragePersister, dehydrateOptions };

const getRootSafeAreaEdges = (): Edge[] => {
  if (Platform.OS === 'ios') {
    return ['top', 'left', 'right'];
  }
  if (Platform.OS === 'android') {
    return ['top'];
  }
  return [];
};

const ROOT_SAFE_AREA_EDGES: Edge[] = getRootSafeAreaEdges();

function AppStack() {
  const { t } = useTranslation();
  useReportVersion();

  // Detail routes (lead/*, linkedin-search) now live in
  // the (app) group and are titled/animated by its own Stack. This root Stack
  // only owns the auth boundary + public screens and the (app) group itself.
  const screenTitles: Record<string, string> = {
    index: t('layout.title'),
    login: t('layout.login'),
    '(app)': t('layout.dashboard'),
    invite: 'Accept Invitation',
    'privacy-policy': 'Privacy Policy',
  };

  return (
    <Stack
      screenOptions={({ route }) => ({
        headerShown: false,
        title: screenTitles[route.name],
        contentStyle: { backgroundColor: colors.background.primary },
        animation: 'fade' as const,
        animationDuration: Platform.OS === 'web' ? 150 : 200,
      })}
    />
  );
}

function RootLayout() {
  useNotificationListeners();
  useScreenTracking();

  const navigationRef = useNavigationContainerRef();

  useEffect(() => {
    if (navigationRef?.current) {
      registerNavigationContainer(navigationRef);
    }
  }, [navigationRef]);

  const [fontsLoaded, fontError] = useFonts({
    DMSans_400Regular,
    DMSans_500Medium,
    DMSans_600SemiBold,
    DMSans_700Bold,
    Sora_600SemiBold,
    Sora_800ExtraBold,
    Manrope_400Regular,
    Manrope_500Medium,
    GeistMono_400Regular,
    GeistMono_600SemiBold,
  });

  useEffect(() => {
    if (fontsLoaded || fontError) {
      SplashScreen.hideAsync();
    }
  }, [fontsLoaded, fontError]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      return;
    }

    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');

    const prev = {
      htmlBg: html.style.backgroundColor,
      htmlOverscroll: html.style.overscrollBehaviorY,
      htmlOverflowX: html.style.overflowX,
      bodyBg: body.style.backgroundColor,
      bodyMinHeight: body.style.minHeight,
      bodyMargin: body.style.margin,
      bodyOverscroll: body.style.overscrollBehaviorY,
      bodyOverflowX: body.style.overflowX,
      bodyWidth: body.style.width,
      rootBg: root?.style.backgroundColor ?? '',
      rootMinHeight: root?.style.minHeight ?? '',
      rootOverflowX: root?.style.overflowX ?? '',
      rootWidth: root?.style.width ?? '',
    };

    html.style.backgroundColor = colors.background.primary;
    html.style.overscrollBehaviorY = 'none';
    html.style.overflowX = 'hidden';

    body.style.backgroundColor = colors.background.primary;
    body.style.minHeight = '100%';
    body.style.margin = '0';
    body.style.overscrollBehaviorY = 'none';
    body.style.overflowX = 'hidden';
    // Use width:100% (relative to html) instead of 100vw — 100vw includes the
    // vertical scrollbar gutter and shifts content left under the scrollbar.
    body.style.width = '100%';

    if (root) {
      root.style.backgroundColor = colors.background.primary;
      root.style.minHeight = '100dvh';
      root.style.overflowX = 'hidden';
      root.style.width = '100%';
    }

    return () => {
      html.style.backgroundColor = prev.htmlBg;
      html.style.overscrollBehaviorY = prev.htmlOverscroll;
      html.style.overflowX = prev.htmlOverflowX;

      body.style.backgroundColor = prev.bodyBg;
      body.style.minHeight = prev.bodyMinHeight;
      body.style.margin = prev.bodyMargin;
      body.style.overscrollBehaviorY = prev.bodyOverscroll;
      body.style.overflowX = prev.bodyOverflowX;
      body.style.width = prev.bodyWidth;

      if (root) {
        root.style.backgroundColor = prev.rootBg;
        root.style.minHeight = prev.rootMinHeight;
        root.style.overflowX = prev.rootOverflowX;
        root.style.width = prev.rootWidth;
      }
    };
  }, []);

  if (!fontsLoaded && !fontError) {
    return null;
  }

  return (
    <GestureHandlerRootView style={styles.gestureRoot}>
      <ErrorBoundary>
        <SafeAreaProvider>
          <SafeAreaView style={styles.safeArea} edges={ROOT_SAFE_AREA_EDGES}>
            <TranslationProvider>
              <ToastProvider>
                <ModalProvider>
                  <MaybeMixpanelProvider>
                    <AuthProvider>
                      <RegisterMixpanelSuperProps />
                      <AccountLanguageBridge />
                      <LayoutChromeProvider>
                        <PersistQueryClientProvider
                          client={queryClient}
                          persistOptions={persistOptions}
                        >
                          <StatusBar
                            style="light"
                            backgroundColor={colors.background.primary}
                            translucent={Platform.OS === 'android' ? false : undefined}
                          />
                          <OrgCacheReset />
                          <OfflineBanner />
                          <AppStack />
                        </PersistQueryClientProvider>
                      </LayoutChromeProvider>
                    </AuthProvider>
                  </MaybeMixpanelProvider>
                </ModalProvider>
              </ToastProvider>
            </TranslationProvider>
          </SafeAreaView>
        </SafeAreaProvider>
      </ErrorBoundary>
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  gestureRoot: {
    flex: 1,
  },
  safeArea: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
});

export default Sentry.wrap(RootLayout);
